import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { checkCaseId } from '../services/api'
import OsteoLogo from '../components/Logo'

// Accepts auto-generated O3D-YYYY-MM-DD-NNNN and custom IDs
// (uppercase alphanumeric + hyphens/underscores, 3–50 chars)
const CASE_ID_REGEX = /^[A-Z0-9][A-Z0-9_-]{2,49}$/

export default function LandingPage() {
  const [input,   setInput]   = useState('')
  const [error,   setError]   = useState(null)
  const [loading, setLoading] = useState(false)
  const inputRef              = useRef(null)
  const navigate              = useNavigate()

  // Normalise input as the user types: uppercase, strip spaces
  function handleChange(e) {
    const raw = e.target.value.toUpperCase().replace(/\s/g, '')
    setInput(raw)
    setError(null)
  }

  // Auto-insert the "O3D-" prefix if the user starts typing without it.
  // Guard against modifier combos (Ctrl+V, Cmd+V, Cmd+A, etc.) so paste works.
  function handleKeyDown(e) {
    if (input === '' && !e.ctrlKey && !e.metaKey && e.key !== 'Backspace' && !e.key.startsWith('Arrow')) {
      const char = e.key.toUpperCase()
      if (/^[A-Z0-9]$/.test(char) && char !== 'O') {
        e.preventDefault()
        setInput('O3D-' + char)
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    const trimmed = input.trim()

    if (!trimmed) {
      setError('Please enter your Case ID.')
      inputRef.current?.focus()
      return
    }

    if (!CASE_ID_REGEX.test(trimmed)) {
      setError('Invalid format. Case IDs look like O3D-K7QM2P9X — check your coordinator provided the correct ID.')
      inputRef.current?.focus()
      return
    }

    setLoading(true)
    try {
      await checkCaseId(trimmed)
      navigate(`/upload/${trimmed}`)
    } catch (err) {
      const msg = err.response?.data?.error || 'Unable to verify Case ID. Please try again.'
      setError(msg)
      inputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="card max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <a
            href="https://www.osteo3d.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-brand-500 transition-colors mb-4"
          >
            ← Back to osteo3d.com
          </a>
          <div className="flex justify-center mb-5">
            <a href="https://www.osteo3d.com" target="_blank" rel="noopener noreferrer">
              <OsteoLogo size="lg" />
            </a>
          </div>
          <h2 className="text-lg font-semibold text-gray-700">Medical Data Upload Portal</h2>
          <p className="text-gray-400 text-sm mt-0.5">DICOM Data Submission</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="caseId" className="block text-sm font-medium text-gray-700 mb-1.5">
            Case ID
          </label>

          <input
            ref={inputRef}
            id="caseId"
            type="text"
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="O3D-K7QM2P9X"
            maxLength={50}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={loading}
            className={[
              'input font-mono tracking-widest text-center text-lg',
              error ? 'border-red-400 focus:ring-red-400' : '',
            ].join(' ')}
          />

          {/* Error */}
          {error && (
            <p className="mt-2 text-sm text-red-600 flex items-start gap-1.5">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-4"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Spinner /> Verifying…
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </form>

        {/* Help text */}
        <p className="mt-5 text-center text-xs text-gray-400">
          Your Case ID was provided by your Osteo3d coordinator.
          <br />
          Contact them if you don&apos;t have one.
        </p>
      </div>

      {/* Admin link — subtle, bottom of page */}
      <a
        href="/admin/login"
        className="mt-8 text-xs text-gray-300 hover:text-gray-400 transition-colors"
      >
        Admin portal
      </a>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}
